import { supabase } from '../lib/supabase';

// ── Bundle Profile — Linked Partners ──────────────────────────────────────

export const bundleLinkedPartnersService = {
  async getByBundle(catalogBundleId) {
    const { data, error } = await supabase
      ?.from('bundle_partners')
      ?.select(`
        id,
        partner_id,
        link_status,
        notes,
        created_at,
        partners (
          id, legal_name, brand_name, tier, status, country
        )
      `)
      ?.eq('catalog_bundle_id', catalogBundleId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      partnerId: row?.partner_id,
      linkStatus: row?.link_status,
      notes: row?.notes,
      createdAt: row?.created_at,
      partnerName: row?.partners?.brand_name || row?.partners?.legal_name || '—',
      partnerTier: row?.partners?.tier,
      partnerStatus: row?.partners?.status,
      partnerCountry: row?.partners?.country,
    }));
  },

  async assign(catalogBundleId, partnerId, notes = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('bundle_partners')
      ?.upsert({
        catalog_bundle_id: catalogBundleId,
        partner_id: partnerId,
        assigned_by: user?.id || null,
        notes: notes || null,
        link_status: 'active',
      }, { onConflict: 'catalog_bundle_id,partner_id' })
      ?.select(`id, partner_id, link_status, notes, created_at, partners(id, legal_name, brand_name, tier, status, country)`)
      ?.single();
    if (error) throw error;
    return {
      id: data?.id,
      partnerId: data?.partner_id,
      linkStatus: data?.link_status,
      notes: data?.notes,
      createdAt: data?.created_at,
      partnerName: data?.partners?.brand_name || data?.partners?.legal_name || '—',
      partnerTier: data?.partners?.tier,
      partnerStatus: data?.partners?.status,
      partnerCountry: data?.partners?.country,
    };
  },

  async unlink(linkId) {
    const { error } = await supabase?.from('bundle_partners')?.delete()?.eq('id', linkId);
    if (error) throw error;
  },

  async countByBundle(catalogBundleId) {
    const { count, error } = await supabase
      ?.from('bundle_partners')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('catalog_bundle_id', catalogBundleId)
      ?.eq('link_status', 'active');
    if (error) return 0;
    return count || 0;
  },
};

// ── Bundle Lifecycle ───────────────────────────────────────────────────────

export const bundleLifecycleService = {
  async getByBundle(catalogBundleId) {
    const { data, error } = await supabase
      ?.from('bundle_lifecycle')
      ?.select(`
        id, stage, stage_date, notes, expected_eol_date, created_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_bundle_id', catalogBundleId)
      ?.order('stage_date', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      stage: row?.stage,
      stageDate: row?.stage_date,
      notes: row?.notes,
      expectedEolDate: row?.expected_eol_date,
      createdAt: row?.created_at,
      changedByName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'System',
    }));
  },

  async addStage(catalogBundleId, stageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('bundle_lifecycle')
      ?.insert({
        catalog_bundle_id: catalogBundleId,
        stage: stageData?.stage,
        stage_date: stageData?.stageDate || new Date()?.toISOString()?.split('T')?.[0],
        notes: stageData?.notes || null,
        expected_eol_date: stageData?.expectedEolDate || null,
        changed_by: user?.id || null,
      })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ── Bundle Updates / News Feed ─────────────────────────────────────────────

export const bundleUpdatesService = {
  async getByBundle(catalogBundleId) {
    const { data, error } = await supabase
      ?.from('bundle_updates')
      ?.select(`
        id, update_type, title, content, created_at, updated_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_bundle_id', catalogBundleId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      updateType: row?.update_type,
      title: row?.title,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      authorName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'Admin',
    }));
  },

  async create(catalogBundleId, updateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('bundle_updates')
      ?.insert({
        catalog_bundle_id: catalogBundleId,
        update_type: updateData?.updateType || 'general',
        title: updateData?.title,
        content: updateData?.content || null,
        posted_by: user?.id || null,
      })
      ?.select(`id, update_type, title, content, created_at, user_profiles(full_name, email)`)
      ?.single();
    if (error) throw error;
    return {
      id: data?.id,
      updateType: data?.update_type,
      title: data?.title,
      content: data?.content,
      createdAt: data?.created_at,
      authorName: data?.user_profiles?.full_name || data?.user_profiles?.email || 'Admin',
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('bundle_updates')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Bundle Reference Projects ──────────────────────────────────────────────

export const bundleReferenceProjectsService = {
  async getByBundle(catalogBundleId) {
    const { data, error } = await supabase
      ?.from('bundle_reference_projects')
      ?.select('*')
      ?.eq('catalog_bundle_id', catalogBundleId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      projectName: row?.project_name,
      partnerName: row?.partner_name,
      sector: row?.sector,
      outcomeSummary: row?.outcome_summary,
      createdAt: row?.created_at,
    }));
  },

  async create(catalogBundleId, projectData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('bundle_reference_projects')
      ?.insert({
        catalog_bundle_id: catalogBundleId,
        project_name: projectData?.projectName,
        partner_name: projectData?.partnerName || null,
        sector: projectData?.sector || 'general',
        outcome_summary: projectData?.outcomeSummary || null,
        created_by: user?.id || null,
      })
      ?.select()
      ?.single();
    if (error) throw error;
    return {
      id: data?.id,
      projectName: data?.project_name,
      partnerName: data?.partner_name,
      sector: data?.sector,
      outcomeSummary: data?.outcome_summary,
      createdAt: data?.created_at,
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('bundle_reference_projects')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Bundle Partner Count (for catalog list) ────────────────────────────────

export const bundlePartnerCountService = {
  async getAllCounts() {
    const { data, error } = await supabase
      ?.from('bundle_partners')
      ?.select('catalog_bundle_id')
      ?.eq('link_status', 'active');
    if (error) return {};
    const counts = {};
    (data || [])?.forEach(row => {
      counts[row?.catalog_bundle_id] = (counts?.[row?.catalog_bundle_id] || 0) + 1;
    });
    return counts;
  },
};
